import { useCallback } from 'react'
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import { useParams } from 'react-router-dom'

import { useMapApiKey } from 'src/shared/api/google-maps/use-map-api-key'

import type { SearchFilters } from '../../../schemas/marketplace/filters'
import type { Profession } from '../../../schemas/profession'
import useActiveFilterStore from '../../../stores/marketplace/active-filter'
import { formatLocationFromUrl } from '../../../utils/format-location'

export const useMarketplaceSearch = () => {
  const { data: mapApiKey } = useMapApiKey()
  const { placesService } = usePlacesAutocompleteService({
    apiKey: mapApiKey,
    debounce: 500,
  })
  const { profession } = useParams()
  const { city, state } = useParams()

  // Use the common formatting utility to process URL parameters
  const { city: formattedCity, state: formattedState, location: formattedLocation } = formatLocationFromUrl(city, state)

  const { setSubmittedFilters, setSelectedProfession, setLocationSearchInput, updateSubmittedFilters } =
    useActiveFilterStore()

  const initializeFromUrl = useCallback(() => {
    if (profession || formattedCity || formattedState) {
      const filters: Partial<SearchFilters> = {}

      if (profession) {
        filters.profession = profession as Profession
        setSelectedProfession(profession as Profession)
      }

      if (formattedCity && formattedState) {
        filters.city = formattedCity
        filters.state = formattedState
        filters.location = formattedLocation
        setLocationSearchInput(formattedLocation)

        // Use placesService.getDetails directly with a constructed query
        if (placesService) {
          placesService.findPlaceFromQuery(
            {
              query: `${formattedCity}, ${formattedState}, US`,
              fields: ['geometry'],
            },
            (placeDetails: google.maps.places.PlaceResult[] | null) => {
              if (placeDetails?.[0]?.geometry?.location) {
                updateSubmittedFilters({
                  latitude: placeDetails[0].geometry.location.lat(),
                  longitude: placeDetails[0].geometry.location.lng(),
                })
              } else {
                setSubmittedFilters(filters)
              }
            },
          )
        } else {
          setSubmittedFilters(filters)
        }
      } else {
        setSubmittedFilters(filters)
      }
    }
  }, [
    profession,
    formattedCity,
    formattedState,
    formattedLocation,
    setSubmittedFilters,
    setSelectedProfession,
    setLocationSearchInput,
    updateSubmittedFilters,
    placesService,
  ])

  return {
    params: { profession, city: formattedCity, state: formattedState },
    initializeFromUrl,
  }
}
